/* This month's pump prices (fuelPrices/{org}__{YYYY-MM}) for the console's
   Fuel prices page (2026-10-28) — the classic FuelPricesPage's reads, writes
   and rules, without its screen:
   - a station on the list prices a fill there; anywhere else uses the EPRA
     ceiling (or the regional estimate until EPRA is on file);
   - Zana-tier schools pin up to five primary stations, and Zana fills the rest
     from Places Nearby around the school pin, at the ceiling, never over a
     price the school typed (💸 Places is a paid Google call, one per press);
   - "Refresh EPRA prices" asks the payments backend to re-read EPRA.
   A save that fails is reported, not swallowed (classic only logged it). */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { openLatLng } from '../../utils/locationCrypto';
import { fetchNearbyFuelStations, NEARBY_FUEL_STATION_TARGET, PRIMARY_FUEL_STATION_LIMIT } from '../../utils/nearbyFuelStations';
import { currentMonthKey, FuelPriceStation, FuelPricesDoc, StationFuelType } from '../../components/FuelPricesPage';

const BASE = (process.env.REACT_APP_PAYMENTS_URL || '').replace(/\/$/, '');
export { PRIMARY_FUEL_STATION_LIMIT, NEARBY_FUEL_STATION_TARGET };
export type { StationFuelType };

export interface StationRow { placeId: string; name: string; diesel?: number; petrol?: number; primary: boolean }
type Answer = { ok: true; message?: string } | { ok: false; message: string };

export function monthWords(key: string): string {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return key;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function useFuelPrices(orgCode: string, zanaTier: boolean) {
  const month = useMemo(() => currentMonthKey(), []);
  const docId = `${orgCode}__${month}`;
  const [loading, setLoading] = useState(true);
  const [stations, setStations] = useState<FuelPriceStation[]>([]);
  const [source, setSource] = useState<FuelPricesDoc['source']>('admin');
  const [epraTown, setEpraTown] = useState<string | null>(null);
  const [carriedFrom, setCarriedFrom] = useState<string | undefined>();
  const [lastEpra, setLastEpra] = useState<{ diesel: number | null; petrol: number | null; at: Date | null } | null>(null);
  const [regional, setRegional] = useState<number | null>(null);
  const [caps, setCaps] = useState<{ diesel: number | null; petrol: number | null }>({ diesel: null, petrol: null });
  const [homePin, setHomePin] = useState<{ lat: number; lng: number } | null>(null);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  useEffect(() => {
    if (!orgCode) return undefined;
    let live = true;
    // The pin is sealed at rest on newer schools; openLatLng reads both shapes.
    getDoc(doc(db, 'organizations', orgCode))
      .then((s) => openLatLng((s.data() || {}).homeLocations))
      .then((p) => { if (live) setHomePin(p); })
      .catch(() => undefined);
    const unsub = onSnapshot(doc(db, 'fuelPrices', docId), (snap) => {
      if (!live) return;
      const d = snap.data() as FuelPricesDoc | undefined;
      if (d) {
        setStations((Array.isArray(d.stations) ? d.stations : []).map((s) => ({ ...s, fuelType: s.fuelType === 'petrol' ? 'petrol' : 'diesel', primary: Boolean(s.primary) })));
        setSource(d.source ?? 'admin');
        setEpraTown(d.epraTown ?? null);
        setCarriedFrom(d.carriedFrom);
        setLastEpra(d.lastEpraCaps && (d.lastEpraCaps.diesel != null || d.lastEpraCaps.petrol != null)
          ? { diesel: d.lastEpraCaps.diesel ?? null, petrol: d.lastEpraCaps.petrol ?? null, at: d.lastEpraRefreshAt && typeof (d.lastEpraRefreshAt as Timestamp).toDate === 'function' ? (d.lastEpraRefreshAt as Timestamp).toDate() : null }
          : null);
        setRegional(d.regionalDefaultPerL ?? null);
        const byType = d.regionalDefaultPerLByType || d.caps || {};
        setCaps({ diesel: byType.diesel ?? d.regionalDefaultPerL ?? null, petrol: byType.petrol ?? null });
      }
      setLoading(false);
    }, () => { if (live) setLoading(false); });
    return () => { live = false; unsub(); };
  }, [orgCode, docId]);

  const primaryCount = useMemo(() => new Set(stations.filter((s) => s.primary && s.placeId).map((s) => s.placeId)).size, [stations]);
  const placeCount = useMemo(() => new Set(stations.map((s) => s.placeId).filter(Boolean)).size, [stations]);
  const primaryFull = primaryCount >= PRIMARY_FUEL_STATION_LIMIT;

  const persist = useCallback(async (next: FuelPriceStation[], nextSource: FuelPricesDoc['source'] = 'admin'): Promise<Answer> => {
    const before = stations;
    setStations(next);
    try {
      await setDoc(doc(db, 'fuelPrices', docId), {
        orgCode, month, source: nextSource, stations: next,
        regionalDefaultPerL: regional,
        regionalDefaultPerLByType: { diesel: caps.diesel ?? regional, petrol: caps.petrol },
        updatedBy: auth.currentUser?.uid ?? null, updatedAt: serverTimestamp(),
      }, { merge: true });
      setSource(nextSource);
      setCarriedFrom(undefined);
      return { ok: true };
    } catch {
      setStations(before);
      return { ok: false, message: 'Couldn’t save the prices. Check the connection and try again.' };
    }
  }, [stations, docId, orgCode, month, regional, caps]);

  /** Add or update one fuel type at one place. Zana-tier schools: a new station is a primary (five at most). */
  const addStation = useCallback((name: string, placeId: string, fuelType: StationFuelType, price: number): Promise<Answer> => {
    const id = placeId || name.trim();
    const onList = stations.some((s) => s.placeId === id);
    const wasPrimary = stations.some((s) => s.placeId === id && s.primary);
    if (zanaTier && !onList && primaryFull) {
      return Promise.resolve({ ok: false, message: `You already have ${PRIMARY_FUEL_STATION_LIMIT} primary stations. Remove one first, or let Zana fill the rest from nearby.` });
    }
    const key = `${fuelType}:${id}`;
    let next: FuelPriceStation[] = [
      ...stations.filter((s) => `${s.fuelType || 'diesel'}:${s.placeId}` !== key),
      { placeId: id, name: name.trim(), pricePerL: price, fuelType, primary: zanaTier || wasPrimary },
    ];
    if (zanaTier) next = next.map((s) => (s.placeId === id ? { ...s, primary: true } : s));
    return persist(next, 'admin');
  }, [stations, zanaTier, primaryFull, persist]);

  const removePlace = useCallback((placeId: string) => persist(stations.filter((s) => s.placeId !== placeId), 'admin'), [stations, persist]);

  const setPrimary = useCallback((placeId: string, on: boolean): Promise<Answer> => {
    if (on && !stations.some((s) => s.placeId === placeId && s.primary) && primaryFull) {
      return Promise.resolve({ ok: false, message: `Your ${PRIMARY_FUEL_STATION_LIMIT} primary stations are set. Unmark one first.` });
    }
    return persist(stations.map((s) => (s.placeId === placeId ? { ...s, primary: on } : s)), 'admin');
  }, [stations, primaryFull, persist]);

  /** A typed price for one fuel type at a place on the list. */
  const setPrice = useCallback((placeId: string, fuelType: StationFuelType, price: number): Promise<Answer> => {
    const key = `${fuelType}:${placeId}`;
    const sibling = stations.find((s) => s.placeId === placeId);
    if (!sibling) return Promise.resolve({ ok: false, message: 'That station is no longer on the list.' });
    const next = stations.some((s) => `${s.fuelType || 'diesel'}:${s.placeId}` === key)
      ? stations.map((s) => (`${s.fuelType || 'diesel'}:${s.placeId}` === key ? { ...s, pricePerL: price } : s))
      : [...stations, { placeId, name: sibling.name, pricePerL: price, fuelType, primary: Boolean(sibling.primary) }];
    return persist(next, 'admin');
  }, [stations, persist]);

  /** 💸 One Places Nearby call around the school pin: fill to ~10 places at the ceiling. */
  const seedNearby = useCallback(async (): Promise<Answer> => {
    if (!homePin) return { ok: false, message: 'Your school has no map pin yet, so Zana can’t search near it. Ask Trasse to set it.' };
    const dieselCap = caps.diesel ?? regional;
    const petrolCap = caps.petrol;
    if (dieselCap == null && petrolCap == null) return { ok: false, message: 'There is no EPRA ceiling on file yet. Refresh EPRA prices first, then try again.' };
    if (!apiKey) return { ok: false, message: 'Station search needs the Google Maps key on this site.' };
    const found = await fetchNearbyFuelStations(apiKey, homePin.lat, homePin.lng);
    if (!found.ok) return { ok: false, message: found.message };
    const byKey = new Map<string, FuelPriceStation>(stations.map((s) => [`${s.fuelType || 'diesel'}:${s.placeId}`, s]));
    const places = new Set(stations.map((s) => s.placeId).filter(Boolean));
    let added = 0;
    for (const place of found.places) {
      const already = places.has(place.placeId);
      if (!already && places.size >= NEARBY_FUEL_STATION_TARGET) break;
      for (const ft of ['diesel', 'petrol'] as StationFuelType[]) {
        const price = ft === 'diesel' ? dieselCap : petrolCap;
        if (price == null || !Number.isFinite(price) || price <= 0) continue;
        const key = `${ft}:${place.placeId}`;
        const had = byKey.get(key);
        // A typed or primary price is kept; only the name is refreshed.
        byKey.set(key, had ? { ...had, name: place.name } : { placeId: place.placeId, name: place.name, pricePerL: Number(price), fuelType: ft, primary: false });
      }
      if (!already) { places.add(place.placeId); added += 1; }
    }
    const saved = await persist(Array.from(byKey.values()), placeCount === 0 && added > 0 ? 'zana' : source === 'admin' ? 'admin' : 'zana');
    if (!saved.ok) return saved;
    return { ok: true, message: added > 0 ? `Zana added ${added} station${added === 1 ? '' : 's'} near your school, at the ceiling. Change any price — yours wins.` : 'Nothing new nearby — those stations are already on your list.' };
  }, [homePin, caps, regional, apiKey, stations, persist, placeCount, source]);

  /** The backend re-reads EPRA's monthly ceilings for this school. */
  const refreshEpra = useCallback(async (): Promise<Answer> => {
    if (!BASE) return { ok: false, message: 'The payments server isn’t set up on this site.' };
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'Sign in again, then try once more.' };
    try {
      const res = await fetch(`${BASE}/api/premium/fuel/refresh-prices`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ orgCode }),
      });
      const body = await res.json().catch(() => ({}));
      if (body.caps) setCaps({ diesel: body.caps.diesel ?? null, petrol: body.caps.petrol ?? null });
      if (!res.ok || !body.ok) {
        return { ok: false, message: body.fallbackApplied ? 'EPRA couldn’t be reached, so regional prices are on file until it is back.' : (body.message || `EPRA couldn’t be read (${res.status}).`) };
      }
      return { ok: true, message: body.message || 'EPRA prices refreshed.' };
    } catch {
      return { ok: false, message: 'Couldn’t reach the payments server. Try again in a minute.' };
    }
  }, [orgCode]);

  /** One row per place: diesel and petrol side by side, primaries first. */
  const rows = useMemo<StationRow[]>(() => {
    const m = new Map<string, StationRow>();
    stations.forEach((s) => {
      const cur = m.get(s.placeId) || { placeId: s.placeId, name: s.name, primary: Boolean(s.primary) };
      cur.name = s.name || cur.name;
      if (s.primary) cur.primary = true;
      if ((s.fuelType || 'diesel') === 'petrol') cur.petrol = s.pricePerL; else cur.diesel = s.pricePerL;
      m.set(s.placeId, cur);
    });
    return Array.from(m.values()).sort((a, b) => Number(b.primary) - Number(a.primary) || a.name.localeCompare(b.name));
  }, [stations]);

  return {
    month, loading, rows, source, epraTown, carriedFrom, lastEpra, caps: { diesel: caps.diesel ?? regional, petrol: caps.petrol },
    homePin, apiKey, primaryCount, placeCount, primaryFull,
    addStation, removePlace, setPrimary, setPrice, seedNearby, refreshEpra,
  };
}
